import gql from 'graphql-tag'
import { CheckoutSubmitModule as ParentCheckoutSubmitModule } from '../submit'
import { getProvider } from '@/apollo'
import { splitError } from '@/helper/splitError'

const initialState = {
  consents: {},
  result: {},
  processId: null,
  contractDocumentUrl: null,
  backendErrorCode: null,
  backendErrorMessage: null
}

const CheckoutSubmitModule = {
  namespaced: true,
  state: initialState,
  getters: {
    deferredResponse: ParentCheckoutSubmitModule.getters.deferredResponse,
    getSuccessUrl: ParentCheckoutSubmitModule.getters.getSuccessUrl,
    consents: ParentCheckoutSubmitModule.getters.consents,
    processId: ParentCheckoutSubmitModule.getters.processId,
    checkoutInputCore: ParentCheckoutSubmitModule.getters.checkoutInputCore,
    checkoutPortabilityCore: ParentCheckoutSubmitModule.getters.checkoutPortabilityCore,
    contractDocumentUrl: ParentCheckoutSubmitModule.getters.contractDocumentUrl,
    contractId: ParentCheckoutSubmitModule.getters.contractId,
    backendErrorCode: state => state.backendErrorCode,
    backendErrorMessage: state => state.backendErrorMessage,
    getPromoCodeSubmit (state, getters, rootState, rootGetters) {
      return rootGetters['promotion/promoCode/validPromoCode']
    },
    getTariffOptions (state, getters, rootState, rootGetters) {
      if (rootGetters['checkout/tariff/selectedAboOptionId']) {
        return {
          TariffoptionInput: {
            tariffoptionId: rootGetters['checkout/tariff/selectedAboOptionId']
          }
        }
      } else {
        return null
      }
    },
    getSuccessIdent: ParentCheckoutSubmitModule.getters.getSuccessIdent
  },
  mutations: {
    storeResult: ParentCheckoutSubmitModule.mutations.storeResult,
    storeProcessId: ParentCheckoutSubmitModule.mutations.storeProcessId,
    storeConsents: ParentCheckoutSubmitModule.mutations.storeConsents,
    storeContractDocumentUrl: ParentCheckoutSubmitModule.mutations.storeContractDocumentUrl,
    SET_BACKEND_ERROR_CODE (state, code) {
      state.backendErrorCode = code
    },
    SET_BACKEND_ERROR_MESSAGE (state, code) {
      state.backendErrorMessage = code
    },
    resetState (state) {
      // we must leave the result state to ensure we have contract id for the success page
      const result = state.result
      Object.assign(state, initialState)
      state.result = result
    }
  },
  actions: {
    async checkoutInitiate ({ commit, state, getters, rootGetters }, { nextStep, stay = false }) {
      commit('clearMessages', {}, { root: true })
      commit('SET_BACKEND_ERROR_CODE', null)
      commit('SET_BACKEND_ERROR_MESSAGE', null)

      let retailerId = rootGetters.retailerId
      let campaignCode = null
      campaignCode = rootGetters['kwk/campaignId']
      if (rootGetters['checkout/tariff/finalTariffId'] === '162' && rootGetters['kwk/kwkOrderByLink']) {
        retailerId = '30861183'
      }
      const apolloClient = getProvider().defaultClient
      return apolloClient.mutate({
        mutation: gql`
          mutation checkout ($orderInput: OrderInitiateInput!) {
            orderInitiate (orderInitiate: $orderInput){
              documentUrl
              processId
            }
          }`,
        variables: {
          orderInput: {
            ...getters.checkoutInputCore, // inkl ident data
            ...getters.checkoutPortabilityCore,
            product: {
              tariffIdentifier: rootGetters['checkout/tariff/finalTariffId'],
              promotions: null,
              tariffoptions: getters.getTariffOptions,
              article: null,
              promoCode: getters.getPromoCodeSubmit,
              campaignCode,
              simType: rootGetters['checkout/personalData/simType']
            },
            orderContext: {
              market: 'PUC',
              retailerId,
              eid: rootGetters['checkout/personalData/eID']
            }
          }
        }
      }).then((response) => {
        commit('storeProcessId', response.data.orderInitiate.processId)
        commit('storeContractDocumentUrl', response.data.orderInitiate.documentUrl + '?contentDisposition=inline')
        if (!stay) {
          commit('checkout/goToStep', nextStep, { root: true })
        }
        return response.data.orderInitiate.documentUrl
      }).catch((error) => {
        let errorCode = 500
        let errorMessage = 'Es ist ein technischer Fehler aufgetreten.'

        // get error code and message
        if (splitError(error)[0].extensions.magma_code) {
          errorCode = splitError(error)[0].extensions.magma_code
        }
        if (splitError(error)[0].message) {
          errorMessage = splitError(error)[0].message
        }

        // build error message
        if (errorMessage.indexOf(errorCode) === -1) {
          errorMessage = errorMessage + ' (Fehler: ' + errorCode + ')'
        }

        // update state
        commit('SET_BACKEND_ERROR_CODE', errorCode)
        commit('SET_BACKEND_ERROR_MESSAGE', errorMessage)

        splitError(error).forEach(e => commit('addError', e, { root: true }))
      })
    },
    async checkoutConfirm ({ commit, state, getters, rootGetters }, { nextStep = 'success', consents }) {
      commit('clearMessages', {}, { root: true })
      commit('SET_BACKEND_ERROR_CODE', null)
      commit('SET_BACKEND_ERROR_MESSAGE', null)

      const apolloClient = getProvider().defaultClient
      return apolloClient.mutate({
        mutation: gql`
          mutation checkout ($orderConfirmInput: OrderConfirmInput!) {
            orderConfirm (orderConfirm: $orderConfirmInput){
              contractId,
              customerId
              deferredIdentificationResponse {
                actionID,
                identProvider,
                url
              }
            }
          }`,
        variables: {
          orderConfirmInput: {
            brand: rootGetters.brand,
            processId: getters.processId,
            orderConsent: {
              adConsent: consents.contact,
              passData: consents.exchange,
              agb: true,
              contractTermsVersion: '1.0'
            }
          }
        }
      }).then((response) => {
        commit('storeConsents', consents)
        commit('storeResult', response.data.orderConfirm)
        commit('Transaction/removeTransactionId', 'activation', { root: true })
      }).catch((error) => {
        let errorCode = 500
        let errorMessage = 'Es ist ein technischer Fehler aufgetreten.'

        // get error code and message
        if (splitError(error)[0].extensions.magma_code) {
          errorCode = splitError(error)[0].extensions.magma_code
        }
        if (splitError(error)[0].message) {
          errorMessage = splitError(error)[0].message
        }

        // build error message
        if (errorMessage.indexOf(errorCode) === -1) {
          errorMessage = errorMessage + ' (Fehler: ' + errorCode + ')'
        }

        // update state
        commit('SET_BACKEND_ERROR_CODE', errorCode)
        commit('SET_BACKEND_ERROR_MESSAGE', errorMessage)

        splitError(error).forEach(e => commit('addError', e, { root: true }))
      })
    },
    async belatedPortingInitiate ({ commit, state, getters, rootGetters }, { nextStep, stay = false }) {
      commit('clearMessages', {}, { root: true })
      commit('SET_BACKEND_ERROR_CODE', null)
      commit('SET_BACKEND_ERROR_MESSAGE', null)

      const apolloClient = getProvider().defaultClient
      const currentCustomer = rootGetters['checkout/mnpData/contractHolder']
      const contractHolder = {
        gender: 'FEMALE',
        firstName: currentCustomer.firstName,
        lastName: currentCustomer.lastName,
        birthdate: currentCustomer.birthDate
      }
      return apolloClient.mutate({
        mutation: gql`
          mutation belatedPorting ($mpnInInput: InitiateMnpInput!) {
            initiateMnpIn (initiateMnpInput: $mpnInInput){
              contractUrl
              processId
            }
          }`,
        variables: {
          mpnInInput: {
            person: contractHolder,
            portation: rootGetters['checkout/mnpData/portationCore'],
            consent: true, // boolean
            tariffId: rootGetters['checkout/tariff/finalTariffId'],
            product: {
              tariffIdentifier: rootGetters['checkout/tariff/finalTariffId'],
              promotions: null,
              tariffoptions: getters.getTariffOptions,
              article: null,
              promoCode: getters.getPromoCodeSubmit,
              simType: rootGetters['checkout/mnpData/simType']
            }
          }
        }
      }).then((response) => {
        commit('storeProcessId', response.data.initiateMnpIn.processId)
        commit('storeContractDocumentUrl', response.data.initiateMnpIn.contractUrl)
        if (!stay) {
          commit('checkout/goToStep', nextStep, { root: true })
        }
        return response.data.initiateMnpIn.processId
      }).catch((error) => {
        let errorCode = 500
        let errorMessage = 'Es ist ein technischer Fehler aufgetreten.'
        // get error code and message
        if (splitError(error)[0].extensions?.magma_code) {
          errorCode = splitError(error)[0].extensions.magma_code
        }

        if (splitError(error)[0].message) {
          errorMessage = splitError(error)[0].message
        }

        // build error message
        if (errorMessage.indexOf(errorCode) === -1) {
          errorMessage = errorMessage + ' (Fehler: ' + errorCode + ')'
        }
        // update state
        commit('SET_BACKEND_ERROR_CODE', errorCode)
        commit('SET_BACKEND_ERROR_MESSAGE', errorMessage)

        splitError(error).forEach(e => commit('addError', e, { root: true }))
      })
    },
    async belatedPortingConfirm ({ commit, state, getters, rootGetters }, { nextStep = 'success', consents }) {
      commit('clearMessages', {}, { root: true })
      commit('SET_BACKEND_ERROR_CODE', null)
      commit('SET_BACKEND_ERROR_MESSAGE', null)
      const apolloClient = getProvider().defaultClient
      return apolloClient.mutate({
        mutation: gql`
          mutation belatedPorting ($mpnInInput: ConfirmMnpInput!) {
            confirmMnpIn (confirmMnpInput: $mpnInInput){
              contractId,
              success
            }
          }`,
        variables: {
          mpnInInput: {
            processId: getters.processId
          }
        }
      }).then((response) => {
        commit('storeConsents', consents)
        commit('storeResult', response.data.confirmMnpIn)
        commit('Transaction/removeTransactionId', 'confirmMnpIn', { root: true })
      }).catch((error) => {
        let errorCode = 500
        let errorMessage = 'Es ist ein technischer Fehler aufgetreten.'

        // get error code and message
        if (splitError(error)[0].extensions.magma_code) {
          errorCode = splitError(error)[0].extensions.magma_code
        }
        if (splitError(error)[0].message) {
          errorMessage = splitError(error)[0].message
        }

        // build error message
        if (errorMessage.indexOf(errorCode) === -1) {
          errorMessage = errorMessage + ' (Fehler: ' + errorCode + ')'
        }

        // update state
        commit('SET_BACKEND_ERROR_CODE', errorCode)
        commit('SET_BACKEND_ERROR_MESSAGE', errorMessage)

        splitError(error).forEach(e => commit('addError', e, { root: true }))
      })
    },
    resetSubmitDefaultState ({ commit, state }) {
      // initialize
      commit('clearMessages', {}, { root: true })
      commit('resetState', state)
    }
  }

}

export {
  CheckoutSubmitModule
}
